import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import { useFormContext } from "../../context/Form_context";
import Loader from "../../Common/loader/Loader";
import Notification from "../../utils/Notification";
import { renderTitleWithGradient } from "../../utils/helper";
import "./ContactUs.css";
import {
  FaUser,
  FaEnvelope,
  FaTag,
  FaMessage,
  FaLocationDot,
  FaPhone,
  FaArrowRight,
  FaCompass,
} from "react-icons/fa6";

/* ---------- Scroll Reveal Component (Framer Motion) ---------- */
const Reveal = ({ children, className = "", delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.08 }}
      transition={{
        duration: 0.7,
        delay: delay / 1000,
        ease: [0.16, 1, 0.3, 1],
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const email_validation = /\S+@\S+\.\S+/;
const phone_validation = /^\d{10}$/;

const ContactUs = () => {
  const {
    contact_us_loading,
    contact_us_data,
    GetContactUs,
    PostExpoContactUs,
    expo_contact_us_loading,
  } = useFormContext();

  useEffect(() => {
    GetContactUs();
  }, []); // eslint-disable-line

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const resetForm = () => {
    setName("");
    setEmail("");
    setPhone("");
    setSubject("");
    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (email && !email_validation.test(email)) {
      return Notification("warning", "Please enter a valid email address!");
    }

    if (phone && !phone_validation.test(phone)) {
      return Notification(
        "warning",
        "Please enter a valid 10 digit phone number!",
      );
    }

    const formdata = new FormData();
    formdata.append("name", name);
    formdata.append("email", email);
    formdata.append("phone_no", phone);
    formdata.append("subject", subject);
    formdata.append("message", message);

    // Form fakat success thay tyare j clear thase
    const success = await PostExpoContactUs(formdata);
    if (success) {
      resetForm();
    }
  };

  if (contact_us_loading) {
    return <Loader fullScreen={true} text="Loading Contact Us..." />;
  }

  return (
    <div className="cnt-page">
      <Helmet>
        <title>Contact Us | Bharat Buildcon</title>
      </Helmet>
      {/* Dynamic Ambient Blur Glow Orbs */}
      <div className="cnt-bg-glow cnt-bg-glow--saffron" />
      <div className="cnt-bg-glow cnt-bg-glow--blue" />
      <div className="cnt-bg-glow cnt-bg-glow--gold" />
      <div className="cnt-bg-glow cnt-bg-glow--emerald" />

      {/* ---------------- BREADCRUMB & UNIQUE HERO HEADER ---------------- */}
      <section className="cnt-header-section">
        <div className="cnt-container">
          <Reveal>
            <div className="clean-breadcrumb-wrapper">
              <nav className="about-style-breadcrumb" aria-label="Breadcrumb">
                <Link to="/" className="bc-home-link">
                  HOME
                </Link>
                <span className="bc-slash">/</span>
                <span className="bc-active-page">CONTACT US</span>
              </nav>
            </div>
          </Reveal>

          {contact_us_data?.contact_sub_heading && (
            <Reveal delay={150}>
              <div className="cnt-badge-chip">
                <span className="cnt-badge-dot" />
                <span>{contact_us_data?.contact_sub_heading}</span>
              </div>
            </Reveal>
          )}

          {contact_us_data?.contact_main_heading && (
            <Reveal delay={250}>
              <h1 className="cnt-hero-title">
                {renderTitleWithGradient(contact_us_data?.contact_main_heading)}
              </h1>
            </Reveal>
          )}

          {contact_us_data?.contact_description && (
            <Reveal delay={350}>
              <div
                className="cnt-hero-subtitle"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    contact_us_data?.contact_description,
                  ),
                }}
              />
            </Reveal>
          )}

          {/* Glassmorphic Media Feature Card */}
          {contact_us_data?.contact_detail_image_full_path && (
            <Reveal delay={450}>
              <div className="cnt-hero-banner-card">
                <div className="cnt-hero-bg-media">
                  <img
                    src={contact_us_data?.contact_detail_image_full_path}
                    alt={contact_us_data?.name || ""}
                    className="cnt-hero-img"
                  />

                  <div className="cnt-hero-overlay" />
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* ---------------- SECTION 1: VISIT US (UNIQUE LUXURY DESIGN WITH CLEAN CONTENT) ---------------- */}
      <section className="cnt-visit-section">
        <div className="cnt-container">
          <Reveal>
            <div className="cnt-section-badge-wrap">
              <span className="cnt-pill-badge cnt-badge-saffron">
                <FaCompass className="cnt-badge-icon-left" /> DIRECT LOCATION
                &amp; CONTACT
              </span>
            </div>
            <h2 className="cnt-section-heading">
              <span className="cnt-gradient-text">Visit Us</span>
            </h2>
          </Reveal>

          {/* Unique 3-Card Glass Grid Layout */}
          <div className="cnt-unique-vu-grid">
            {/* Card 1: Corporate HQ & Address */}
            <Reveal delay={100} className="cnt-vu-card-wrap">
              <div className="cnt-vu-card cnt-vu-card--featured">
                <div className="cnt-vu-card-top">
                  <div className="cnt-vu-icon-wrap cnt-icon--saffron">
                    <FaLocationDot />
                  </div>
                  <span className="cnt-vu-badge cnt-pill--saffron">
                    HEADQUARTERS
                  </span>
                </div>

                <h3 className="cnt-vu-card-title">Corporate Office Address</h3>
                {contact_us_data?.head_contact_detail && (
                  <div
                    className="cnt-vu-card-desc"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        contact_us_data?.head_contact_detail,
                      ),
                    }}
                  />
                )}

                <div className="cnt-vu-card-actions">
                  <a
                    href={contact_us_data?.venue_link}
                    target="_blank"
                    rel="noreferrer"
                    className="cnt-vu-btn cnt-btn--saffron"
                  >
                    <FaLocationDot /> <span>Get Directions</span>
                  </a>
                </div>
              </div>
            </Reveal>

            {/* Card 2: Phone Helplines */}
            <Reveal delay={200} className="cnt-vu-card-wrap">
              <div className="cnt-vu-card cnt-vu-card--blue">
                <div className="cnt-vu-card-top">
                  <div className="cnt-vu-icon-wrap cnt-icon--blue">
                    <FaPhone />
                  </div>
                  <span className="cnt-vu-badge cnt-pill--blue">
                    DIRECT HELPLINES
                  </span>
                </div>

                <h3 className="cnt-vu-card-title">Phone Lines</h3>
                <div className="cnt-vu-phone-rows">
                  {contact_us_data?.domestic_mobile1 && (
                    <a
                      href={`tel:${contact_us_data?.domestic_mobile1?.replace(/\s+/g, "")}`}
                      className="cnt-vu-p-row cnt-vu-p-row--link"
                      onClick={() => {
                        const val = contact_us_data?.domestic_mobile1;
                        if (val && navigator?.clipboard) {
                          navigator.clipboard.writeText(val);
                          Notification(
                            "success",
                            `Copied phone number ${val} to clipboard!`,
                          );
                        }
                      }}
                    >
                      <span className="cnt-vu-phone-num">
                        {contact_us_data?.domestic_mobile1}
                      </span>
                      <span
                        className="cnt-vu-action-icon-btn cnt-action-btn--blue"
                        title="Direct Call Desk 1"
                      >
                        <FaPhone />
                      </span>
                    </a>
                  )}

                  {contact_us_data?.domestic_mobile2 && (
                    <a
                      href={`tel:${contact_us_data?.domestic_mobile2?.replace(/\s+/g, "")}`}
                      className="cnt-vu-p-row cnt-vu-p-row--link"
                      onClick={() => {
                        const val = contact_us_data?.domestic_mobile2;
                        if (val && navigator?.clipboard) {
                          navigator.clipboard.writeText(val);
                          Notification(
                            "success",
                            `Copied phone number ${val} to clipboard!`,
                          );
                        }
                      }}
                    >
                      <span className="cnt-vu-phone-num">
                        {contact_us_data?.domestic_mobile2}
                      </span>
                      <span
                        className="cnt-vu-action-icon-btn cnt-action-btn--blue"
                        title="Direct Call Desk 2"
                      >
                        <FaPhone />
                      </span>
                    </a>
                  )}
                </div>
              </div>
            </Reveal>

            {/* Card 3: Email Desk & Direct Support */}
            <Reveal delay={300} className="cnt-vu-card-wrap">
              <div className="cnt-vu-card cnt-vu-card--green">
                <div className="cnt-vu-card-top">
                  <div className="cnt-vu-icon-wrap cnt-icon--green">
                    <FaEnvelope />
                  </div>
                  <span className="cnt-vu-badge cnt-pill--green">
                    EMAIL DESK
                  </span>
                </div>

                <h3 className="cnt-vu-card-title">Official Email</h3>
                <div className="cnt-vu-phone-rows">
                  {contact_us_data?.domestic_email && (
                    <a
                      href={`mailto:${contact_us_data?.domestic_email}`}
                      className="cnt-vu-p-row cnt-vu-p-row--link cnt-vu-p-row--email"
                      onClick={() => {
                        const val = contact_us_data?.domestic_email;
                        if (val && navigator?.clipboard) {
                          navigator.clipboard.writeText(val);
                          Notification(
                            "success",
                            `Copied email ${val} to clipboard!`,
                          );
                        }
                      }}
                    >
                      <span
                        className="cnt-vu-phone-num cnt-vu-email-num"
                        title={contact_us_data?.domestic_email}
                      >
                        {contact_us_data?.domestic_email}
                      </span>
                      <span
                        className="cnt-vu-action-icon-btn cnt-action-btn--emerald"
                        title="Send Direct Email"
                      >
                        <FaEnvelope />
                      </span>
                    </a>
                  )}

                  {contact_us_data?.inquiry_email && (
                    <a
                      href={`mailto:${contact_us_data?.inquiry_email}`}
                      className="cnt-vu-p-row cnt-vu-p-row--link cnt-vu-p-row--email"
                      onClick={() => {
                        const val = contact_us_data?.inquiry_email;
                        if (val && navigator?.clipboard) {
                          navigator.clipboard.writeText(val);
                          Notification(
                            "success",
                            `Copied email ${val} to clipboard!`,
                          );
                        }
                      }}
                    >
                      <span
                        className="cnt-vu-phone-num cnt-vu-email-num"
                        title={contact_us_data?.inquiry_email}
                      >
                        {contact_us_data?.inquiry_email}
                      </span>
                      <span
                        className="cnt-vu-action-icon-btn cnt-action-btn--emerald"
                        title="Support Desk Email"
                      >
                        <FaEnvelope />
                      </span>
                    </a>
                  )}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------------- SECTION 2: GET IN TOUCH (SLIDING DIAGONALS ANIMATED BACKGROUND EFFECT) ---------------- */}
      <section className="cnt-form-section">
        <div className="cnt-container">
          <Reveal>
            <h2 className="cnt-section-heading">
              Get In <span className="cnt-gradient-text">Touch</span>
            </h2>
          </Reveal>

          <Reveal delay={200}>
            {/* Standalone Centered Form Wrapper */}
            <div className="cnt-standalone-form-wrap">
              <div className="cnt-form-card-main">
                {/* Animated Top Accent Shimmer Line */}
                <div className="cnt-form-top-accent-line" />

                <form onSubmit={handleSubmit} className="cnt-form">
                  <div className="cnt-form-header">
                    <div className="cnt-fh-badge-row">
                      <span className="cnt-pulse-green-dot" />
                      <span className="cnt-fh-badge-text">
                        LIVE RESPONSE DESK ACTIVE
                      </span>
                    </div>
                    <h3 className="cnt-fh-title">
                      Send Your Message Direct To Our Support Desk
                    </h3>
                  </div>

                  {/* Your Name (full width) */}
                  <div className="cnt-field-group">
                    <label htmlFor="name" className="cnt-label">
                      Your Name <span className="cnt-required">*</span>
                    </label>
                    <div className="cnt-input-wrapper">
                      <FaUser className="cnt-field-icon" />
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="cnt-input"
                      />
                    </div>
                  </div>

                  {/* Email & Phone side by side (2-column row) */}
                  <div className="cnt-form-row-2col">
                    {/* Your Email */}
                    <div className="cnt-field-group">
                      <label htmlFor="email" className="cnt-label">
                        Email Address <span className="cnt-required">*</span>
                      </label>
                      <div className="cnt-input-wrapper">
                        <FaEnvelope className="cnt-field-icon" />
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="cnt-input"
                        />
                      </div>
                    </div>

                    {/* Phone Number */}
                    <div className="cnt-field-group">
                      <label htmlFor="phone_no" className="cnt-label">
                        Phone Number <span className="cnt-required">*</span>
                      </label>
                      <div className="cnt-input-wrapper">
                        <FaPhone className="cnt-field-icon" />
                        <input
                          type="tel"
                          id="phone_no"
                          name="phone_no"
                          value={phone}
                          onChange={(e) =>
                            setPhone(
                              e.target.value.replace(/\D/g, "").slice(0, 10),
                            )
                          }
                          className="cnt-input"
                          placeholder="Enter 10 digit phone number"
                          inputMode="numeric"
                          maxLength={10}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Subject */}
                  <div className="cnt-field-group">
                    <label htmlFor="subject" className="cnt-label">
                      Subject / Topic <span className="cnt-required">*</span>
                    </label>
                    <div className="cnt-input-wrapper">
                      <FaTag className="cnt-field-icon" />
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="cnt-input"
                      />
                    </div>
                  </div>

                  {/* Your Message */}
                  <div className="cnt-field-group">
                    <label htmlFor="message" className="cnt-label">
                      Your Message
                    </label>
                    <div className="cnt-input-wrapper">
                      <FaMessage className="cnt-field-icon cnt-icon-textarea" />
                      <textarea
                        id="message"
                        name="message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        className="cnt-textarea"
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="cnt-submit-wrap">
                    <button
                      type="submit"
                      disabled={expo_contact_us_loading}
                      className="cnt-btn-submit-premium"
                    >
                      {expo_contact_us_loading ? (
                        <>
                          <span className="cnt-btn-spinner" />
                          <span>SENDING MESSAGE...</span>
                        </>
                      ) : (
                        <>
                          <span>SUBMIT MESSAGE</span>
                          <FaArrowRight className="cnt-btn-arrow" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
